import { inject, observer } from 'mobx-react';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Icon } from '../components/Icon';
import {
  AppContextType,
  DIALOG_TYPE,
  DIALOG_STATUS,
} from '@byted-cg/linkchat-plugin';
import { Button } from '@byted-cg/linkchat-ui';
import { IAppContext } from '@byted-cg/linkchat-core';
import classes from '../constants/classes';
import { SEND_ORIGIN } from '../constants/message';
import { prefixCls } from '../utils/style';
import LazyComponent from './LazyComponent';
import { IQuickButton, IQuickButtonsConfig, QuickTypes } from './type';
import { debounce } from 'lodash-es';

export type QuickButtonsRef = {
  getQuickBtnsHeight: () => any;
};

export interface IQuickButtonsOptions {
  config?: IQuickButtonsConfig;
  appContext?: AppContextType;
}
const debounceFn = debounce((target) => {
  target && target?.click();
}, 300);

const QuickButtons = React.forwardRef<QuickButtonsRef, IQuickButtonsOptions>(
  ({ config = [], appContext }, ref) => {
    const [show, setShow] = useState(false);
    const [showPrevNext, setShowPrevNext] = useState(false);
    const quickBtnsContentRef = useRef<HTMLDivElement>(null);
    const quickBtnsWrapperRef = useRef<HTMLDivElement>(null);
    const dialogType = Number(appContext?.dialog?.ext?.type);
    const dialogStatus = Number(appContext?.dialog?.ext?.status);

    useEffect(() => {
      if (appContext?.dialogInfo?.id) {
        !show && setShow(true);
      }
    }, [appContext?.dialogInfo?.id]);

    const useFunction =
      (config: IQuickButton) =>
      (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        switch (config.type) {
          case QuickTypes.cmd:
            appContext?.cmd?.send(config.data);
            break;
          case QuickTypes.url:
            appContext?.jsBrige?.openWindow(config.data);
            break;
          case QuickTypes.message:
            appContext?.sendMessage &&
              appContext.sendMessage({
                ...config.data,
                origin: SEND_ORIGIN.QUICK_BUTTON,
              });
            break;
          default:
            const { func, ...props } = config;
            if (func && typeof func === 'function') {
              func(appContext, props);
            }
            break;
        }
      };

    const onScrollPrev = useCallback(() => {
      if (quickBtnsWrapperRef.current && quickBtnsContentRef.current) {
        quickBtnsWrapperRef.current.scroll({
          left:
            quickBtnsWrapperRef.current.scrollLeft -
              quickBtnsWrapperRef.current.offsetWidth <=
            0
              ? 0
              : quickBtnsWrapperRef.current.scrollLeft -
                quickBtnsWrapperRef.current.offsetWidth,
          behavior: 'smooth',
        });
      }
    }, []);
    const onScrollNext = useCallback(() => {
      if (quickBtnsWrapperRef.current && quickBtnsContentRef.current) {
        quickBtnsWrapperRef.current.scroll({
          left:
            quickBtnsWrapperRef.current.scrollLeft +
            quickBtnsWrapperRef.current.offsetWidth,
          behavior: 'smooth',
        });
      }
    }, []);

    const hanlderQuickBtns = useCallback((e) => {
      try {
        let target = e.target;
        e.nativeEvent.isTrusted && e.stopPropagation();
        if (!e.nativeEvent.isTrusted) return;
        debounceFn(target);
      } catch (error) {}
    }, []);

    useImperativeHandle(ref, (): QuickButtonsRef => {
      return {
        getQuickBtnsHeight: () => quickBtnsWrapperRef?.current?.clientHeight,
      };
    });

    useEffect(() => {
      // 计算子元素的长度合
      if (
        quickBtnsWrapperRef?.current?.offsetWidth &&
        quickBtnsContentRef?.current?.children.length
      ) {
        let childs = quickBtnsContentRef?.current?.children;
        let totleLen = Array.from(childs).reduce(
          (acc, child) => acc + child?.offsetWidth,
          0,
        );
        setShowPrevNext(
          appContext?.query?.isPc &&
            filterButtonConfig.length &&
            totleLen + childs.length * 8 >
              quickBtnsWrapperRef.current?.offsetWidth,
        );
      } else {
        setShowPrevNext(false);
      }
    }, [
      quickBtnsContentRef.current?.children?.length,
      quickBtnsWrapperRef.current,
    ]);

    const filterButtonConfig = useMemo(() => {
      if (!config || !Array.isArray(config)) {
        return [];
      }
      return config?.filter?.((itemConfig) => {
        const display = itemConfig?.data?.display; // default custom
        // With display as default, no return button if no hit
        if (
          (display === 'default' &&
            !(
              (dialogType === DIALOG_TYPE.AGENT &&
                itemConfig?.data?.agentActive) ||
              ([
                DIALOG_TYPE.ROBOT,
                DIALOG_TYPE.TRANING,
                DIALOG_TYPE.OFFLINE,
              ].includes(dialogType) &&
                itemConfig?.data?.robotActive)
            )) ||
          dialogStatus === DIALOG_STATUS.ENDING
        ) {
          return false;
        }
        return true;
      });
    }, [dialogType, dialogStatus, config]);

    // Returns null if there is no shortcut button to display
    if (!filterButtonConfig.length) {
      return null;
    }

    return (
      <div
        className={prefixCls(
          {
            [`${classes.prefix}quickButtons`]: true,
            ['quickButtonsDisabled']: !show,
            ['quickButtonsHidden']: !quickBtnsWrapperRef?.current?.clientHeight,
          },
          appContext?.query?.isPc || false,
        )}
      >
        {showPrevNext ? (
          <div className="actionIcon prevAction" onClick={onScrollPrev}>
            <Icon type="Left" size={14} />
          </div>
        ) : null}
        <div className="quickBtnsWrapper" ref={quickBtnsWrapperRef}>
          <div
            className={prefixCls({
              ['quickBtnsContent']: true,
              ['contentNoPrevNext']: !showPrevNext,
            })}
            ref={quickBtnsContentRef}
            onClickCapture={hanlderQuickBtns}
          >
            {filterButtonConfig.map((itemConfig, index) => {
              return typeof itemConfig?.label === 'string' ? (
                <div
                  className="quickButtonItems"
                  onClick={useFunction(itemConfig)}
                  key={`${itemConfig?.label}_${index}`}
                >
                  <Button size="small" className="quickButton">
                    {itemConfig.label}
                  </Button>
                </div>
              ) : (
                <>
                  {itemConfig?.label ? (
                    <LazyComponent
                      fallback={false}
                      key={`${itemConfig.label.code}--${index}`}
                      config={itemConfig.label}
                      data={itemConfig.data}
                    />
                  ) : null}
                </>
              );
            })}
          </div>
        </div>
        {showPrevNext ? (
          <div className="actionIcon nextAction" onClick={onScrollNext}>
            <Icon type="Right" size={14} />
          </div>
        ) : null}
      </div>
    );
  },
);

export default inject<{
  appContext: IAppContext;
}>(({ appContext }) => ({
  appContext,
}))(observer(QuickButtons));
